import React, { createContext, useContext, useState, useEffect } from 'react';
import { clearSession, isTokenUsable } from '../services/session';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Page d'accueil selon le rôle */
export const homeFor = (role) => ({
  admin: '/dashboard', sous_admin: '/dashboard', rh: '/rh', comptable: '/compta', achats: '/achats',
  briques: '/briques/rapport', resp_briques: '/briques/rapports', vigile: '/garde',
}[role] || '/stock');

const TERRAIN = ['admin', 'sous_admin', 'employee'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    // Un ancien jeton (format d'avant la mise à jour) ou un jeton expiré est écarté
    if (token && userData && isTokenUsable(token)) {
      try {
        setUser(JSON.parse(userData));
      } catch {
        clearSession();
      }
    } else {
      clearSession();
    }
    setLoading(false);
  }, []);

  // Rôles à jour (l'administrateur a pu les modifier depuis la dernière connexion)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!user?.id || !token) return;
    fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => {
        if (me && (me.role !== user.role || JSON.stringify(me.roles) !== JSON.stringify(user.roles)
          || (me.notif_panne === true) !== (user.notif_panne === true) || (me.acces_marches === true) !== (user.acces_marches === true) || (me.notif_hebdo === true) !== (user.notif_hebdo === true) || (me.acces_amortissement === true) !== (user.acces_amortissement === true) || (me.acces_paie !== false) !== (user.acces_paie !== false) || (me.acces_numerotation === true) !== (user.acces_numerotation === true) || (me.acces_direction === true) !== (user.acces_direction === true) || (me.valide_factures === true) !== (user.valide_factures === true))) {
          const next = { ...user, role: me.role, roles: me.roles, notif_panne: me.notif_panne === true, acces_marches: me.acces_marches === true, notif_hebdo: me.notif_hebdo === true, acces_amortissement: me.acces_amortissement === true, acces_paie: me.acces_paie !== false, acces_numerotation: me.acces_numerotation === true, acces_direction: me.acces_direction === true, valide_factures: me.valide_factures === true };
          localStorage.setItem('user', JSON.stringify(next));
          setUser(next);
        }
      })
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const updateUser = (changes) => {
    setUser((prev) => {
      const next = { ...prev, ...changes };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    clearSession();
    setUser(null);
  };

  const roles = user ? (Array.isArray(user.roles) && user.roles.length ? user.roles : [user.role]) : [];
  const has = (r) => roles.includes(r);

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    roles,
    hasRole: has,
    isAdmin: has('admin'),
    isSousAdmin: has('sous_admin'),
    isEmployee: has('employee'),
    isRh: has('rh'),
    isComptable: has('comptable'),
    isAchats: has('achats'),
    // Sans rôle « terrain » (admin, responsable, employé), l'utilisateur ne voit que ses domaines métier
    isMetier: roles.length > 0 && !roles.some((r) => TERRAIN.includes(r)),
    canManageRh: has('admin') || has('rh'),
    canCompta: has('admin') || has('comptable'),
    canGarde: has('admin') || has('sous_admin') || has('vigile'),
    canDepenses: has('admin') || user?.acces_depenses === true,
    canNumeroter: has('admin') || has('sous_admin') || user?.acces_numerotation === true,
    canDirection: has('admin') || has('sous_admin') || user?.valide_factures === true || user?.acces_direction === true,
    canPaie: has('admin') || (has('comptable') && user?.acces_paie !== false),
    canAchats: has('admin') || has('achats'),
    home: homeFor(user?.role),
    canViewDashboard: has('admin') || has('sous_admin'),
    canManageUsers: has('admin'),
    // Valideur des factures et proformas (Kane, PDG, DGA…) : seul à pouvoir les émettre
    canValiderFactures: user?.valide_factures === true,
    // Pannes : déclarées par le responsable de production, suivies aussi par les personnes notifiées
    canDeclarerPannes: has('admin') || has('sous_admin'),
    canSuivrePannes: has('admin') || has('sous_admin') || user?.notif_panne === true,
    // Marchés clients (BC SENELEC, ADL) : administrateur et utilisateurs autorisés
    canMarches: has('admin') || user?.acces_marches === true,
    canHebdo: has('admin') || user?.notif_hebdo === true,
    canAmortissement: has('admin') || user?.acces_amortissement === true,
    // Unité briques & pavés : équipe (saisie) et responsable (suivi, corrections, catalogue)
    canBriques: has('admin') || has('briques') || has('resp_briques'),
    canGererBriques: has('admin') || has('resp_briques'),
    mustChangePassword: !!user?.must_change_password,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
